/* A browser-local snapshot of notices acknowledged by opening the listing.
 * No article-body tracking, account data, network calls or notification counts.
 */
(() => {
  'use strict';
  if (customElements.get('ac-news-status')) return;

  class NewsStatus extends HTMLElement {
    connectedCallback() {
      // Defer until this element's JSON and both header navigations are parsed.
      this.start = () => this.initialize();
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', this.start, { once: true });
      else this.initialize();
    }

    initialize() {
      if (!this.isConnected || this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.header = this.closest('.shopify-section');
      if (!this.header) return;
      this.setVisible(false);
      try {
        this.config = JSON.parse(this.querySelector('script').textContent);
        const c = this.config;
        if (!/^\d+$/.test(c.blogId) || !Number.isFinite(c.now) || !Number.isInteger(c.days) || c.days < 1 || c.days > 60 || !Array.isArray(c.articles)) return;
        this.articles = c.articles.filter(a => /^\d+$/.test(a.id) && Number.isFinite(a.published) && a.published > 0);
        this.startedAt = Date.now();
        const theme = window.Shopify?.theme;
        const scope = theme?.role && theme.role !== 'main' ? `preview-${theme.id}:` : '';
        this.key = `ac-news-confirmed-v1:${scope}${c.blogId}`;
        this.storage = window.localStorage;
        // Reading alone doesn't detect quota/private-mode write failures.
        const stored = this.storage.getItem(this.key);
        this.storage.setItem(this.key, stored || JSON.stringify({ version: 1, articleIds: [] }));
        this.available = true;
      } catch {
        return; // Basic anchors still work; the optional dot stays hidden.
      }

      this.refresh = () => {
        if (!this.available) return;
        try {
          if (this.config.listing && !document.hidden && !window.Shopify?.designMode) this.confirm();
          this.update();
        } catch {
          this.available = false;
          this.setVisible(false);
          clearTimeout(this.timer);
        }
      };
      window.addEventListener('storage', event => {
        if (event.key === this.key || event.key === null) this.refresh();
      }, { signal });
      window.addEventListener('pageshow', this.refresh, { signal });
      document.addEventListener('visibilitychange', this.refresh, { signal });
      this.refresh();
    }

    now() {
      // Anchor to Shopify's Unix time, avoiding an incorrectly set client clock.
      return this.config.now + Math.max(0, Date.now() - this.startedAt) / 1000;
    }

    read() {
      const raw = this.storage.getItem(this.key);
      let value;
      try { value = JSON.parse(raw); } catch { /* Recover only this feature's malformed value. */ }
      return value?.version === 1 && Array.isArray(value.articleIds)
        ? value.articleIds.filter(id => typeof id === 'string' && /^\d+$/.test(id)) : [];
    }

    confirm() {
      const ids = this.read();
      const confirmed = new Set(ids);
      for (const article of this.articles) {
        if (article.published <= this.now()) confirmed.add(article.id);
      }
      if (confirmed.size === ids.length) return;
      // Union keeps an older tab/deleted notice from undoing an acknowledgement.
      this.storage.setItem(this.key, JSON.stringify({
        version: 1,
        confirmedAt: Math.floor(this.now()),
        articleIds: [...confirmed],
      }));
    }

    update() {
      clearTimeout(this.timer);
      const confirmed = new Set(this.read());
      const now = this.now();
      const windowSeconds = this.config.days * 86400;
      const fresh = this.articles.filter(a => a.published <= now && now < a.published + windowSeconds && !confirmed.has(a.id));
      this.setVisible(fresh.length > 0 && !this.config.listing);
      if (fresh.length) {
        const nextExpiry = Math.min(...fresh.map(a => a.published + windowSeconds));
        this.timer = setTimeout(this.refresh, Math.min(2147483647, Math.max(1, (nextExpiry - now) * 1000)));
      }
    }

    setVisible(visible) {
      this.header?.querySelectorAll('[data-ac-news-dot], [data-ac-news-description]').forEach(el => { el.hidden = !visible; });
    }

    disconnectedCallback() {
      document.removeEventListener('DOMContentLoaded', this.start);
      this.controller?.abort();
      this.controller = null;
      clearTimeout(this.timer);
    }
  }
  customElements.define('ac-news-status', NewsStatus);
})();
